import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { PuzzleUpdateNotification, PuzzleUpdateNotificationsModel } from './puzzle-update-notifications.model';

import { DatePipe } from '@angular/common';
import * as actions from './puzzle-update-notifications.actions';
import { patch } from '@ngxs/store/operators';
import { HttpClient } from '@angular/common/http';
import { buildEnv } from 'build-environment';
import { firstValueFrom } from 'rxjs';

const __BUILD_VERSION__: string = buildEnv.buildVersion;

@State<PuzzleUpdateNotificationsModel>({
  name: 'puzzleUpdateNotifications',
  defaults: {
    hasUpdates: false,
    unreadUpdateNotifications: [],
    readUpdateNotifications: [],
    updateNotificationDates: {},
  },
})
@Injectable()
export class PuzzleUpdateNotificationsState {
  constructor(private store: Store, private http: HttpClient) {}

  @Action(actions.MarkAsRead)
  MarkAsRead(state: StateContext<PuzzleUpdateNotificationsModel>, action: actions.MarkAsRead) {
    state.setState(
      patch({
        updateNotificationDates: patch({
          [action.payload.puzzleTypeId]: new DatePipe('en-EN').transform(new Date(), 'YYYY-MM-dd'),
        }),
      })
    );
  }

  @Action(actions.LoadUpdateNotifications)
  async LoadUpdateNotifications(
    state: StateContext<PuzzleUpdateNotificationsModel>,
    action: actions.LoadUpdateNotifications
  ) {
    const puzzleTypeId = action.payload.puzzleTypeId;
    let currentDateString = state.getState().updateNotificationDates[puzzleTypeId];

    if (!currentDateString) {
      // If we haven't opened this app, we set the date to today, so no updates are shown.
      currentDateString = new DatePipe('en-EN').transform(new Date(), 'YYYY-MM-dd');
      state.setState(
        patch({
          updateNotificationDates: patch({
            [action.payload.puzzleTypeId]: currentDateString,
          }),
        })
      );
    }

    let updateData = [];

    if (action.payload.language != null) {
      const updateFileName = 'assets/updates-' + action.payload.language + '.json';

      updateData = await firstValueFrom(
        this.http.get<PuzzleUpdateNotification[]>(updateFileName, {
          responseType: 'json',
          params: {
            version: __BUILD_VERSION__,
          },
        })
      );
    }

    const filteredUpdateNotificationData: PuzzleUpdateNotification[] = updateData
      .filter(
        (u) => !(u.puzzleTypes && u.puzzleTypes.length && u.puzzleTypes.indexOf(action.payload.puzzleTypeId) === -1)
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const tempUpdateNotifications: { [key: string]: { date: Date; notifications: PuzzleUpdateNotification[] } } = {};

    for (const notification of filteredUpdateNotificationData) {
      if (!tempUpdateNotifications.hasOwnProperty(notification.date)) {
        tempUpdateNotifications[notification.date] = {
          date: new Date(notification.date),
          notifications: [],
        };
      }
      tempUpdateNotifications[notification.date].notifications.push(notification);
    }

    const updateNotifications = Object.values(tempUpdateNotifications);
    const currentDate = new Date(currentDateString);

    const readUpdateNotifications = updateNotifications.filter((u) => currentDate >= u.date);
    const unreadUpdateNotifications = updateNotifications.filter((u) => currentDate < u.date);

    let hasUpdates = false;

    if (unreadUpdateNotifications.length) {
      hasUpdates = true;
    }

    state.patchState({
      hasUpdates,
      unreadUpdateNotifications,
      readUpdateNotifications,
    });
  }
}
